import { Card, CardBody } from '@progress/kendo-react-layout'
import React from 'react'

import { Box, type BoxProps } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { ZStack } from '~/components/elements/ZStack'
import { FigureInfoTable } from '~/components/features/FigureInfoTable'
import { exists, truncate } from '~/utils'

import { onCardClickContext } from '../contexts'
import { type DocumentImage } from '../hooks/useFetchDocumentImages'

import { ArchiveButton } from './ArchiveButton'
import { AuthorizationRequiredImage } from './AuthorizationRequiredImage'
import styles from './DocumentImageCard.css'
import { EditButton } from './EditButton'
import { RestoreLink } from './RestoreLink'
import { ShareButton } from './ShareButton'

export type DocumentImageCardProps = {
  documentImage: DocumentImage
}

export const DocumentImageCard = ({
  documentImage,
}: DocumentImageCardProps) => {
  const onCardClick = React.useContext(onCardClickContext)
  const onClick = React.useMemo<BoxProps['onClick']>(
    () =>
      exists(onCardClick)
        ? () => {
            onCardClick?.(documentImage)
          }
        : undefined,
    [documentImage, onCardClick],
  )

  return (
    <Box onClick={onClick}>
      <Card>
        <CardBody>
          <HStack spacing={4}>
            <ZStack>
              <RestoreLink
                className={styles.imgContainer}
                documentImage={documentImage}
              >
                <AuthorizationRequiredImage
                  className={styles.img}
                  src={documentImage.imageURL}
                />
              </RestoreLink>

              {documentImage.figureMeta.isArchivable && (
                <Box alignSelf="start" justifySelf="start">
                  <ArchiveButton documentImage={documentImage} />
                </Box>
              )}
            </ZStack>

            <VStack flex={1} spacing={4}>
              <HStack align="center" justify="space-between" spacing={2}>
                <HStack align="center" spacing={1}>
                  <EditButton documentImage={documentImage} />

                  <RestoreLink documentImage={documentImage}>
                    <Typography variant="heading2">
                      {documentImage.title}
                    </Typography>
                  </RestoreLink>
                </HStack>

                <ShareButton documentImage={documentImage} />
              </HStack>

              {exists(documentImage.comment) && (
                <Typography variant="normal">
                  {truncate(documentImage.comment, 100)}
                </Typography>
              )}

              <FigureInfoTable figureInfo={documentImage.figureInfo} />
            </VStack>
          </HStack>
        </CardBody>
      </Card>
    </Box>
  )
}
